/**
 * useAIChat - Platform-agnostic hook for AI chat with streaming SSE responses
 *
 * Manages ephemeral chat state and streams responses from the AI chat endpoint.
 * Works on both web (cookies) and mobile (Bearer token) via configurable auth headers.
 */

import { useState, useRef, useCallback } from 'react';
import type { ChatMessage, StreamDelta } from '../types/aiChat';

/**
 * Configuration for the useAIChat hook
 */
export interface UseAIChatConfig {
  /** Base URL for the API (e.g., 'https://dev.turn-connect.com/api') */
  baseUrl: string;
  /** Returns auth headers - for mobile returns { Authorization: 'Bearer ...' }, for web returns {} (uses cookies) */
  getAuthHeaders: () => Record<string, string>;
  /** Whether to include credentials (cookies) in fetch - defaults to false */
  includeCredentials?: boolean;
}

/**
 * Return type for useAIChat
 */
export interface UseAIChatResult {
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  sendMessage: (question: string) => Promise<void>;
  clearMessages: () => void;
  cancelStream: () => void;
}

/** Generate a unique ID for messages */
function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

/**
 * Hook for managing AI chat state with streaming SSE responses
 */
export function useAIChat(config: UseAIChatConfig): UseAIChatResult {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(
    async (question: string) => {
      const trimmed = question.trim();
      if (!trimmed || isStreaming) return;

      setError(null);

      // Add user message
      const userMessage: ChatMessage = {
        id: generateId(),
        role: 'user',
        content: trimmed,
        timestamp: new Date(),
      };

      // Add empty assistant message (streaming placeholder)
      const assistantMessage: ChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setIsStreaming(true);

      // Build conversation history for context (exclude the new assistant placeholder)
      const conversationHistory = [...messages, userMessage].map((m) => ({
        role: m.role,
        content: m.content,
      }));

      // Create abort controller for cancellation
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...config.getAuthHeaders(),
        };

        const response = await fetch(`${config.baseUrl}/ai-chat/stream`, {
          method: 'POST',
          headers,
          credentials: config.includeCredentials ? 'include' : 'same-origin',
          signal: abortController.signal,
          body: JSON.stringify({
            question: trimmed,
            conversation_history: conversationHistory,
          }),
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('AI Chat is not available yet. The service may still be setting up.');
          }
          const errorText = await response.text().catch(() => response.statusText);
          throw new Error(`Chat request failed: ${response.status} ${errorText}`);
        }

        let accumulatedText = '';

        const processSSELines = (text: string) => {
          const lines = text.split('\n');
          for (const line of lines) {
            const trimmedLine = line.trim();
            if (!trimmedLine || trimmedLine.startsWith(':')) continue;

            if (trimmedLine.startsWith('data: ')) {
              const data = trimmedLine.slice(6);

              try {
                const delta: StreamDelta = JSON.parse(data);

                if (delta.type === 'text' && delta.text) {
                  accumulatedText += delta.text;
                  const currentText = accumulatedText;

                  setMessages((prev) =>
                    prev.map((m) => (m.id === assistantMessage.id ? { ...m, content: currentText, isStreaming: true } : m))
                  );
                } else if (delta.type === 'done') {
                  const finalText = accumulatedText;
                  setMessages((prev) =>
                    prev.map((m) => (m.id === assistantMessage.id ? { ...m, content: finalText, isStreaming: false } : m))
                  );
                } else if (delta.type === 'error') {
                  throw new Error(delta.error || 'Stream error');
                }
              } catch (parseErr) {
                if (parseErr instanceof SyntaxError) {
                  console.warn('[AIChat] Non-JSON SSE data:', data);
                } else {
                  throw parseErr;
                }
              }
            }
          }
        };

        if (response.body && typeof response.body.getReader === 'function') {
          // Web: Use ReadableStream for true streaming
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n');
            buffer = parts.pop() || '';
            processSSELines(parts.join('\n'));
          }
          // Process any remaining buffer
          if (buffer.trim()) {
            processSSELines(buffer);
          }
        } else {
          // React Native: ReadableStream not supported, read full response
          const text = await response.text();
          processSSELines(text);
        }

        // Ensure streaming is marked as done even if no explicit 'done' event
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantMessage.id ? { ...m, content: accumulatedText || m.content, isStreaming: false } : m))
        );
      } catch (err) {
        // Ignore abort errors (intentional cancellation)
        if (err instanceof Error && err.name === 'AbortError') {
          setMessages((prev) => prev.map((m) => (m.id === assistantMessage.id ? { ...m, isStreaming: false } : m)));
        } else {
          const errorMsg = err instanceof Error ? err.message : 'An error occurred';
          setError(errorMsg);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessage.id
                ? {
                    ...m,
                    content: m.content || 'Sorry, an error occurred. Please try again.',
                    isStreaming: false,
                  }
                : m
            )
          );
        }
      } finally {
        setIsStreaming(false);
        abortControllerRef.current = null;
      }
    },
    [config, isStreaming, messages]
  );

  const clearMessages = useCallback(() => {
    // Cancel any active stream first
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setMessages([]);
    setIsStreaming(false);
    setError(null);
  }, []);

  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  return {
    messages,
    isStreaming,
    error,
    sendMessage,
    clearMessages,
    cancelStream,
  };
}
